/**
 * Les réglages du site : ce que l'équipe peut changer sans passer par le dépôt.
 *
 * ## Ce que ce module garantit
 *
 * Une page qui affiche une photo ne doit jamais dépendre du succès d'une
 * requête. Les valeurs par défaut sont donc celles qui étaient en dur avant
 * cette table : réseau coupé, RLS mal posée, table vide — la page d'accueil
 * reste la page d'accueil. Un réglage absent est un réglage par défaut, jamais
 * une image cassée.
 *
 * ## Pourquoi une URL et pas un chemin de stockage
 *
 * `url` porte une adresse complète. C'est ce qui permet à l'équipe de coller
 * un lien externe aussi bien que de téléverser un fichier, et c'est ce qui a
 * permis de semer la table avec les images déjà affichées : la migration n'a
 * rien changé à l'écran.
 *
 * ## La lecture est ici, l'accès à la base est à côté
 *
 * Ce fichier ne connaît pas Supabase : c'est ce qui permet de le charger dans
 * un test sans variables d'environnement, et de vérifier les replis — la seule
 * chose qui compte vraiment ici — sans réseau. `gateway.supabase.ts` porte les
 * requêtes.
 *
 * ## Les avis sont choisis à la main, et c'est délibéré
 *
 * L'API Places de Google ne rend que cinq avis, « les plus pertinents » ou
 * « les plus récents », sans qu'on puisse choisir lesquels — et ses conditions
 * interdisent de les stocker. « Nos meilleurs avis » n'était donc pas
 * atteignable par cette voie. Ils sont saisis depuis l'admin, avec le lien
 * vers la fiche Google à côté pour que le lecteur puisse vérifier.
 */

/* -------------------------------------------------------------------------- */
/* Les formes                                                                  */
/* -------------------------------------------------------------------------- */

export interface Photo {
  url: string;
  /** Sans lui, l'image n'existe ni pour un lecteur d'écran ni pour Google. */
  alt: string;
}

export interface Temoignage {
  auteur: string;
  texte: string;
  /** Au format `AAAA-MM-JJ`, ou vide si on ne l'a pas notée. */
  date?: string;
}

export interface AvisGoogle {
  /** La fiche Google, pour que le lecteur puisse aller vérifier. */
  lien: string;
  /** La note globale, saisie à la main. `null` si on préfère ne pas l'afficher. */
  note: number | null;
  /** Le nombre d'avis, idem. */
  nombre: number | null;
  temoignages: Temoignage[];
}

export interface Reglages {
  image_accueil: Photo;
  image_connexion: Photo;
  image_newsletter: Photo;
  avis_google: AvisGoogle;
}

export type CleReglage = keyof Reglages;

/* -------------------------------------------------------------------------- */
/* Les valeurs de repli                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Exactement ce qui était en dur avant la table.
 *
 * Ce ne sont pas des valeurs de démonstration : ce sont celles que le site
 * affichait, reprises telles quelles. Une page qui retombe dessus ressemble au
 * site d'hier, pas à un site cassé.
 */
export const PAR_DEFAUT: Reglages = {
  image_accueil: {
    url: 'https://www.eclosion.be/new/wp-content/uploads/2017/11/site-bg-01.jpg',
    alt: 'Des enfants allongés dans l’herbe pendant un stage Éclosion',
  },
  image_connexion: {
    url: 'https://images.pexels.com/photos/8617855/pexels-photo-8617855.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    alt: 'Illustration de la page de connexion',
  },
  image_newsletter: {
    url: 'https://images.pexels.com/photos/3905856/pexels-photo-3905856.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    alt: 'Illustration du bloc newsletter',
  },
  avis_google: {
    lien: 'https://www.google.com/search?q=Eclosion+asbl',
    note: null,
    nombre: null,
    temoignages: [],
  },
};

/* -------------------------------------------------------------------------- */
/* La lecture                                                                  */
/* -------------------------------------------------------------------------- */

const texte = (v: unknown): string => (typeof v === 'string' ? v : '');
const nombreOuNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * Une photo lue depuis la base, ou celle par défaut.
 *
 * Une URL vide retombe sur le défaut : la table accepte n'importe quel jsonb,
 * et un réglage à moitié saisi ne doit pas produire un carré gris.
 */
export function versPhoto(brut: unknown, defaut: Photo): Photo {
  if (!brut || typeof brut !== 'object') return defaut;
  const o = brut as Record<string, unknown>;
  const url = texte(o.url).trim();
  if (!url) return defaut;
  return { url, alt: texte(o.alt).trim() || defaut.alt };
}

/** Les avis lus depuis la base, débarrassés de ce qui n'est pas affichable. */
export function versAvis(brut: unknown, defaut: AvisGoogle): AvisGoogle {
  if (!brut || typeof brut !== 'object') return defaut;
  const o = brut as Record<string, unknown>;

  /*
    Un témoignage sans texte ou sans auteur n'a rien à faire sur la page : il
    n'apporte rien et ressemble à un bogue. On les écarte à la lecture plutôt
    que de demander à chaque écran d'y penser.
  */
  const temoignages = Array.isArray(o.temoignages)
    ? o.temoignages
        .map((t) => {
          const e = (t ?? {}) as Record<string, unknown>;
          return {
            auteur: texte(e.auteur).trim(),
            texte: texte(e.texte).trim(),
            date: texte(e.date).trim() || undefined,
          };
        })
        .filter((t) => t.auteur && t.texte)
    : [];

  return {
    lien: texte(o.lien).trim() || defaut.lien,
    note: nombreOuNull(o.note),
    nombre: nombreOuNull(o.nombre),
    temoignages,
  };
}

/**
 * Un objet quelconque ramené à des réglages complets.
 *
 * C'est l'unique porte d'entrée : la base et le cache du navigateur passent
 * tous les deux par ici. Aucun appelant n'a donc à se demander si la valeur
 * qu'il tient est saine — un champ abîmé retombe sur son défaut, les autres
 * sont gardés.
 */
export function versReglages(brut: unknown): Reglages {
  const o = brut && typeof brut === 'object' ? (brut as Record<string, unknown>) : {};
  return {
    image_accueil: versPhoto(o.image_accueil, PAR_DEFAUT.image_accueil),
    image_connexion: versPhoto(o.image_connexion, PAR_DEFAUT.image_connexion),
    image_newsletter: versPhoto(o.image_newsletter, PAR_DEFAUT.image_newsletter),
    avis_google: versAvis(o.avis_google, PAR_DEFAUT.avis_google),
  };
}

/** Assemble les lignes de la table en un jeu de réglages complet. */
export function depuisLignes(lignes: { cle: string; valeur: unknown }[] | null): Reglages {
  return versReglages(Object.fromEntries((lignes ?? []).map((l) => [l.cle, l.valeur])));
}
